/**
 * feat-270 panel 6 — "Approved by _ on _" on the Lesson identity row.
 *
 * AC2's first state, and **it is NOT a chip.** An approved Lesson adds nothing to
 * its tree row (AT-25's "zero pending renders no badge", and the row has no width
 * to spare), so the fact lives where a reader actually goes to ask about one
 * Lesson: the meta block in the centre pane.
 *
 * **Treatment is `ContentTimestamps`', exactly** — `text-xs text-inkMuted`, label
 * `font-semibold`, no icon, no chip, no colour — because it is the same KIND of
 * thing: a read-only authoring fact attached to the block that names the object.
 * `inkMuted` is AA on every panel ground in this app.
 *
 * ⚠ **An ABSOLUTE date, never `timeAgo()`.** feat-252's split: a detail panel
 * answers *"when was this approved"*, which the reader resolves against calendar
 * knowledge, while the History drawer's relative form is for an event feed. Two
 * timestamp treatments on one screen are fine when each names the question it
 * answers. Do not unify them.
 *
 * ⚠ **HEIGHT BUDGET — this rides the IDENTITY row, not the cream meta strip.**
 * `components.md` / feat-252: that strip is a pinned `flex-wrap`, and one more
 * child wraps it to a second row, costing list rows below. Design's rule is
 * *"look one row up before one row down"* — the identity row has a free trailing
 * edge and absorbs a 12px child at ZERO vertical cost, which is exactly what
 * `ContentTimestamps variant="inline"` already does beside it.
 */

import React from 'react';
import { APPROVAL_EDITOR_COPY as E } from '../approvalCopy';
import {
  actorDisplayName,
  type ApprovalActor,
  type LessonApprovalStatus,
} from '../../../../services/curriculumApprovalService';

export interface LessonApprovalRow {
  state: LessonApprovalStatus;
  pendingCount: number;
  approvedBy: ApprovalActor | null;
  approvedAt: string | null;
}

/** Absolute, and deliberately the same shape `ContentTimestamps` prints. */
function formatApprovedAt(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const LessonApprovalStamp: React.FC<{ row: LessonApprovalRow | null }> = ({ row }) => {
  // Absent, not empty: no row means the flag is off or the state is unknown, and
  // in both cases this says nothing at all (AT-29, §10.10).
  if (!row) return null;

  if (row.state === 'never_approved') {
    return (
      <span
        data-testid="lesson-approval-stamp"
        className="text-xs text-attention-on shrink-0"
      >
        {E.panelNeverApproved}
      </span>
    );
  }

  if (row.state === 'pending') {
    // Neutral, like the chip and for the same reason (NOTES §3): pending is the
    // frequent state, and colour spent on it leaves none for the rare one.
    return (
      <span data-testid="lesson-approval-stamp" className="text-xs text-inkMuted shrink-0">
        {E.panelPending(row.pendingCount)}
      </span>
    );
  }

  if (!row.approvedBy || !row.approvedAt) return null;

  return (
    <span data-testid="lesson-approval-stamp" className="text-xs text-inkMuted shrink-0">
      <span className="font-semibold">Approved by</span>{' '}
      {/* AT-26c's rule holds here too: a null `name` renders the EMAIL, never
          "Unknown" — null is the departing-editor case, and "Unknown" erases the
          one identifier that still resolves. */}
      {actorDisplayName(row.approvedBy)} <span className="font-semibold">on</span>{' '}
      {formatApprovedAt(row.approvedAt)}
    </span>
  );
};
